import { format, isSameDay } from "date-fns";
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getTimeframeErrorMessage } from ".";
import { Timeframe, VACANCY_START_AVAILABILITY } from "../../../models/vacancy";
import Calendar from "../Icons/Calendar";
import RemoveCircle from "../Icons/RemoveCircle";
import Time from "../Icons/Time";
import CustomSelect from "../Select";
import ErrorMessage from "../Tooltips/ErrorMessage";
import {
  filterEndTimeOptions,
  filterStartTimeOptions,
} from "./timeframeHelpers";

// 1. Custom Date Input for DatePicker
const CustomDateInput = React.forwardRef<
  HTMLDivElement,
  {
    range: [Date | null, Date | null] | null;
    onClick?: () => void;
    disabled: boolean;
    isSingleDate?: boolean;
  }
>(({ range, disabled = false, isSingleDate = false, onClick }, ref) => {
  // Date Format Handler
  const formatDateRange = (
    range: [Date | null, Date | null] | null
  ): string => {
    if (!range) return "";
    const [start, end] = range;
    if (isSingleDate && end) return format(end, "dd MMM yyyy");
    if (!start) return "";
    if (!end || isSameDay(start, end)) return format(start, "dd MMM yyyy");
    return `${format(start, "dd MMM yyyy")} - ${format(end, "dd MMM yyyy")}`;
  };

  const displayValue = formatDateRange(range);

  return (
    <div
      className="form-control w-100 d-flex gap-8 align-items-center justify-content-between"
      onClick={onClick}
      ref={ref}
      style={{ cursor: "pointer" }}
    >
      <div
        className={`d-flex gap-8 align-items-center ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        } ${!displayValue ? "text-grey-medium" : ""}`}
      >
        <Calendar />
        {displayValue || "Select a date or date range..."}
      </div>
      <svg
        className={`min-w-12px`}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10.5 4.0575L9.4425 3L6 6.435L2.5575 3L1.5 4.0575L6 8.5575L10.5 4.0575Z"
          fill="#757575"
        />
      </svg>
    </div>
  );
});

/**
 * Returns the next valid start time in "HH:mm" format,
 * rounded up to the next 15-minute interval from the current time.
 * If the time exceeds the day, it returns "23:59".
 */
function getRoundedStartTimeValue(): string {
  const now = new Date();
  const totalMinutes = now.getHours() * 60 + now.getMinutes();

  // Round up to the next 15-minute mark (strictly after current minute)
  const roundedTotal = Math.ceil((totalMinutes + 1) / 15) * 15;

  // Handle case where time overflows beyond 23:59
  if (roundedTotal >= 24 * 60) {
    return "23:59"; // Final fallback option
  }

  const roundedHour = Math.floor(roundedTotal / 60);
  const roundedMinutes = roundedTotal % 60;

  return `${roundedHour.toString().padStart(2, "0")}:${roundedMinutes.toString().padStart(2, "0")}`; // Format: "HH:mm"
}

function getDateRange(date: Date): [Date, Date] {
  const startDate = new Date(); // Use the selected date
  const endDate = new Date(date); // Same day unless user picks a range
  return [startDate, endDate];
}
type TimeframeUpdate = Partial<Omit<Timeframe, "id">>; // all fields except id

interface TimeframeBlockProps {
  tf: Timeframe;
  index: number;
  startAvailability: VACANCY_START_AVAILABILITY;
  timeOptions: { label: string; value: string }[];
  onChange: (id: number, updates: TimeframeUpdate) => void;
  onRemove?: (id: number) => void;
  disabled?: boolean;
  error: string | string[];
}

const TimeframeBlock: React.FC<TimeframeBlockProps> = ({
  tf,
  index,
  startAvailability,
  timeOptions,
  disabled = false,
  error,
  onChange,
  onRemove,
}) => {
  // Extract and format error from Formik
  const timeframeError = getTimeframeErrorMessage(error);

  const fromNow = startAvailability === VACANCY_START_AVAILABILITY.NOW;
  const today = tf?.created_at ? new Date(tf.created_at) : new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const isFutureDate =
    startAvailability === VACANCY_START_AVAILABILITY.FUTURE_DATE;

  const minSelectableDate = isFutureDate ? tomorrow : today;

  // Function to filter out weekends (Saturday = 6, Sunday = 0)
  const isWeekday = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6; // 0 = Sunday, 6 = Saturday
  };

  return (
    <div className="d-flex flex-column gap-10 position-relative">
      <div className="p-10px border border-grey-light rounded-2 d-flex flex-column gap-10">
        {/* Date Picker */}
        {fromNow && index === 0 ? (
          <DatePicker
            filterDate={isWeekday}
            selected={tf.date ? tf.date[1] : null}
            disabled={disabled}
            onChange={
              disabled
                ? undefined
                : (date: Date | null, event?: React.SyntheticEvent) => {
                    if (!date) return;

                    const [start, end] = getDateRange(date);

                    // Update both date and start_time at once
                    onChange(tf.id, {
                      date: [start, end],
                      start_time: getRoundedStartTimeValue(),
                    });
                  }
            }
            customInput={
              <CustomDateInput
                range={tf.date}
                disabled={disabled}
                isSingleDate={true}
              />
            }
            dateFormat="dd MMM yyyy"
            minDate={minSelectableDate}
          />
        ) : (
          <DatePicker
            filterDate={isWeekday}
            selected={tf.date ? tf.date[0] : null}
            disabled={disabled}
            onChange={
              disabled
                ? undefined
                : (range) => {
                    onChange(tf.id, {
                      date: range as [Date | null, Date | null],
                    });
                  }
            }
            onCalendarClose={() => {
              if (tf.date && tf.date[0] && !tf.date[1]) {
                onChange(tf.id, { date: [tf.date[0], tf.date[0]] });
              }
            }}
            startDate={tf.date?.[0]}
            endDate={tf.date?.[1]}
            selectsRange
            customInput={
              <CustomDateInput range={tf.date} disabled={disabled} />
            }
            dateFormat="dd MMM yyyy"
            minDate={minSelectableDate}
          />
        )}
        {/* Time Select */}
        <div className="d-flex flex-column flex-lg-row gap-10">
          {fromNow && index === 0 ? (
            // Only End Time (NOW)
            <div className="w-100">
              <CustomSelect
                options={filterEndTimeOptions(
                  timeOptions,
                  tf.date?.[0],
                  tf.date?.[1],
                  tf.start_time
                )}
                placeholder="Select end time..."
                value={
                  timeOptions.find((opt) => opt.value === tf.end_time) || null
                }
                disabled={disabled}
                onSelect={(selected) => {
                  if (!disabled) {
                    onChange(tf.id, { end_time: String(selected.value) });
                  }
                }}
                enableFilter={false}
                icon={<Time />}
                scrollToTimeOnOpen={
                  timeOptions.find((opt) => opt.value === tf.end_time)?.value ||
                  "07:00"
                }
              />
            </div>
          ) : (
            <>
              {/* Start Time */}
              <div className="w-100">
                <CustomSelect
                  options={
                    tf.date
                      ? filterStartTimeOptions(
                          timeOptions,
                          tf.date?.[0],
                          tf?.created_at ? new Date(tf.created_at) : new Date(),
                          isFutureDate
                        )
                      : []
                  }
                  placeholder="Select start time..."
                  value={
                    timeOptions.find((opt) => opt.value === tf.start_time) ||
                    null
                  }
                  disabled={disabled}
                  maxOptionHeight={250}
                  onSelect={(selected) => {
                    if (disabled) return;

                    const startValue = selected.value as string;

                    if (isFutureDate) {
                      // For "On a future date" vacancies, default end time to 5PM; user can still change it
                      onChange(tf.id, { start_time: startValue, end_time: "17:00" });
                    } else {
                      // Find the index of the selected start time
                      const startIndex = timeOptions.findIndex(
                        (opt) => opt.value === startValue
                      );

                      // Preferred steps in 15-min increments: 2hr=8, 1.5hr=6, 1hr=4, 30min=2
                      const preferredSteps = [8, 6, 4, 2];

                      // Find the first valid end time based on available options
                      const endValue =
                        preferredSteps
                          .map((step) => timeOptions[startIndex + step]?.value)
                          .find((val) => val !== undefined) ||
                        timeOptions[startIndex + 1]?.value; // fallback to next 15-min slot

                      onChange(tf.id, {
                        start_time: startValue,
                        end_time: endValue,
                      });
                    }
                  }}
                  enableFilter={false}
                  icon={<Time />}
                  scrollToTimeOnOpen={
                    timeOptions.find((opt) => opt.value === tf.start_time)
                      ?.value || "07:00"
                  }
                />
              </div>

              {/* End Time */}
              <div className="w-100">
                <CustomSelect
                  options={
                    tf.start_time
                      ? filterEndTimeOptions(
                          timeOptions,
                          tf.date?.[0],
                          tf.date?.[1],
                          tf.start_time
                        )
                      : []
                  }
                  placeholder="Select end time..."
                  value={
                    timeOptions.find((opt) => opt.value === tf.end_time) || null
                  }
                  disabled={disabled}
                  onSelect={(selected) => {
                    if (!disabled) {
                      onChange(tf.id, { end_time: String(selected.value) });
                    }
                  }}
                  enableFilter={false}
                  icon={<Time />}
                  scrollToTimeOnOpen={
                    timeOptions.find((opt) => opt.value === tf.end_time)
                      ?.value || "07:00"
                  }
                />
              </div>
            </>
          )}
        </div>
        {/* Display validation error if any */}
        {error && <ErrorMessage message={timeframeError} />}
      </div>
      {/* Remove Button */}
      <>{onRemove && <RemoveCircle onClick={() => onRemove(tf.id)} />}</>
    </div>
  );
};

export default TimeframeBlock;
